import apiClient from './client';

export interface OrderEvent {
  type: string;
  date: string;
  message?: string;
}

export interface OrderNote {
  type: string;
  date: string;
  message: string;
}

export interface Order {
  urn: string;
  uuid: string;
  originator: string;
  requester: string;
  product: string;
  status: string;
  vis?: {
    urn: string;
    vendor: string;
  };
  payloads?: {
    request?: Record<string, unknown>;
    response?: Record<string, unknown>;
  };
  events?: OrderEvent[];
  notes?: OrderNote[];
  parent?: string;
  createdByRule?: string;
  chainId?: string;
  ruleEvaluationEnabled?: boolean;
  message?: string;
  createdAt: string;
  modificationDate?: string;
}

export interface VISStatusResponse {
  urn?: string;
  status: string | null;
  vendor?: string;
  type?: string;
  message?: string;
  reports?: {
    vendorName?: string;
    productType?: string;
    documents?: {
      fileName: string;
      contentType?: string;
    }[];
  }[];
  context?: {
    source?: string;
    errors?: {
      code: string;
      title: string;
      detail: string;
      meta?: Record<string, unknown>;
    }[];
  };
}

export interface OrdersQueryParams {
  id?: string;
  loan?: string;
  offset?: number;
  limit?: number;
  sortField?: string;
  sortOrder?: 'asc' | 'desc';
  status?: string;
  cascade?: string;
}

export interface OrdersListResponse {
  data: Order[];
  total: number;
  offset: number;
  limit: number;
}

export interface CreateOrderRequest {
  product: string;
  payload: Record<string, unknown>;
}

export interface CreateNoteRequest {
  message: string;
  type?: string;
}

export async function queryOrders(params: OrdersQueryParams): Promise<OrdersListResponse> {
  const response = await apiClient.get('/oms/orders', { params });
  const data = response.data;
  if (Array.isArray(data)) {
    return { data, total: data.length, offset: params.offset || 0, limit: params.limit || 25 };
  }
  return data;
}

export async function getOrder(id: string): Promise<Order> {
  const response = await apiClient.get(`/oms/orders/${id}`);
  return response.data;
}

export async function getOrderWithPayloads(id: string): Promise<Order> {
  const response = await apiClient.get(`/oms/orders/${id}/payloads`);
  return response.data;
}

export async function createOrder(request: CreateOrderRequest): Promise<Order> {
  const response = await apiClient.post('/oms/orders', request);
  return response.data;
}

export async function cancelOrder(urn: string, message?: string): Promise<void> {
  await apiClient.post(`/oms/orders/${urn}/cancel`, { message });
}

export async function recoverOrder(urn: string): Promise<void> {
  await apiClient.post(`/oms/orders/${urn}/recover`);
}

export async function executeOrderEvent(id: string, event: string): Promise<void> {
  await apiClient.post(`/oms/orders/${id}/event/${event}`);
}

export async function getOrderNotes(urn: string): Promise<OrderNote[]> {
  const response = await apiClient.get(`/oms/orders/${urn}/notes`);
  const data = response.data;
  // Backend returns { urn, order, messages: [...] }, extract the messages array
  if (Array.isArray(data)) {
    return data;
  }
  if (data?.messages && Array.isArray(data.messages)) {
    return data.messages.map((m: Record<string, unknown>) => ({
      type: (m.from as string) || 'note',
      date: m.date as string,
      message: m.message as string,
    }));
  }
  return [];
}

export async function createOrderNote(urn: string, note: CreateNoteRequest): Promise<void> {
  await apiClient.post(`/oms/orders/${urn}/notes`, note);
}

export async function getVisStatus(id: string): Promise<VISStatusResponse> {
  const response = await apiClient.get(`/oms/orders/${id}/vis-status`);
  return response.data;
}

export async function downloadOrderFile(id: string, fileName: string): Promise<Blob> {
  const response = await apiClient.get(`/oms/orders/${id}/files/${fileName}`, {
    responseType: 'blob',
  });
  return response.data;
}

export async function getOrderLogicProducts(
  lender?: string,
  productUrns?: string[],
): Promise<unknown> {
  const response = await apiClient.get('/oms/orders/order-logic/products', {
    params: { lender, productUrns: productUrns?.join(',') },
  });
  return response.data;
}

export async function cancelOrderLogicChain(chainId: string): Promise<void> {
  await apiClient.post(`/oms/orders/order-logic/${chainId}/cancel`);
}

/**
 * Fetches all orders in a cascade chain using the dedicated chain endpoint.
 * This endpoint does NOT filter by requester, so all chain orders are returned.
 */
export async function getCascadeOrders(chainId: string): Promise<Order[]> {
  const response = await apiClient.get(`/oms/orders/chain/${chainId}`);
  const data = response.data;
  return data?.orders || [];
}

export interface OrderStats {
  total: number;
  byStatus: Record<string, number>;
  byProduct: Record<string, number>;
  byOriginator: Record<string, number>;
}

export interface OrderStatsParams {
  originator?: string;
  product?: string;
  status?: string;
  from?: string;
  to?: string;
}

export async function getOrderStats(params?: OrderStatsParams): Promise<OrderStats> {
  const response = await apiClient.get('/oms/orders/stats', { params });
  return response.data;
}

// --- Timeline ---

export interface TimelineBucket {
  time: string;
  total: number;
  completed: number;
  failed: number;
  processing: number;
  other: number;
}

export interface TimelineResponse {
  buckets: TimelineBucket[];
}

export interface TimelineParams {
  from: string;
  to: string;
  bucketMinutes?: number;
  originator?: string;
}

export async function getOrderTimeline(params: TimelineParams): Promise<TimelineResponse> {
  const response = await apiClient.get('/oms/orders/stats/timeline', { params });
  return response.data;
}

// --- Failures ---

export interface OrderFailure {
  urn: string;
  product: string;
  originator: string;
  status: string;
  message: string;
  createdAt: string;
}

export interface FailuresResponse {
  failures: OrderFailure[];
}

export interface FailuresParams {
  from?: string;
  to?: string;
  limit?: number;
  originator?: string;
}

export async function getOrderFailures(params?: FailuresParams): Promise<FailuresResponse> {
  const response = await apiClient.get('/oms/orders/stats/failures', { params });
  return response.data;
}
